import {
  concatStyleSets,
  FontWeights,
  getHighContrastNoAdjustStyle,
  getSplitButtonClassNames as getFabricSplitButtonClassNames,
  HighContrastSelector,
  IButtonStyles,
  IRawStyle,
  ISplitButtonClassNames,
  ITheme,
  memoizeFunction,
  mergeStyleSets,
} from "@fluentui/react";

import {
  commonHighContrastFocusedDisabledStyles,
  commonHighContrastHoverFocusStyles,
  commonHighContrastOutlineFocusedDisabled,
  commonHighContrastOutlineHoverFocus,
  commonHighContrastOutlineToggledOn,
  getCommonIsFocusVisibleStyles,
  getInnerBorderStyles,
  getStandardControlSemanticColorsWithTheme,
  IButtonClassNames,
  StandardControlSemanticColors,
} from "@1js/acui-common";

import { getAppBaseButtonStyles } from "../AppBaseButton/AppBaseButton.styles";

export const menuButtonClassName = "splitMenuButton";
export const primaryButtonClassName = "splitPrimaryButton";

const splitButtonComponentHighContrastStyles = {
  [HighContrastSelector]: {
    backgroundColor: "Highlight",
    ...getHighContrastNoAdjustStyle(),
  },
};

const menuButtonWidth = 32;
const buttonHeight = 32;
const dividerHeight = 16;

export const baseSplitButtonExpandedCheckedIconStyle = (
  disabled?: boolean
): IRawStyle => {
  return {
    selectors: {
      [HighContrastSelector]: {
        selectors: {
          "svg > path.OfficeIconColors_HighContrast": {
            fill: disabled ? "GrayText" : "HighlightText",
          },
        },
      },
    },
  };
};

export const getClassNamesForButton = memoizeFunction(
  (theme: ITheme, customStyles?: IButtonStyles, hasIcon: boolean = false) => {
    return memoizeFunction(
      (
        _: ITheme,
        __: string,
        ___: string,
        ____: string | undefined,
        _____: string | undefined,
        disabled: boolean,
        checked: boolean,
        expanded: boolean,
        ______: boolean,
        _______: boolean
      ): IButtonClassNames => {
        const styles = concatStyleSets(
          getAppPrimarySplitButtonStyles(theme, hasIcon),
          customStyles
        );
        return mergeStyleSets({
          root: [
            styles.root,
            checked && styles.rootChecked,
            expanded && {
              selectors: {
                ":hover $icon": styles.iconExpandedHovered,
                ":hover $menuIcon": styles.rootExpandedHovered,
                ":hover": styles.rootExpandedHovered,
              },
            },
            disabled && styles.rootDisabled,
            !disabled &&
              !expanded &&
              !checked && {
                selectors: {
                  ":hover": styles.rootHovered,
                  ":hover $icon": styles.iconHovered,
                  ":hover $description": styles.descriptionHovered,
                  ":hover $menuIcon": styles.menuIconHovered,
                  ":focus": styles.rootFocused,
                  ":active": styles.rootPressed,
                  ":active $icon": styles.iconPressed,
                  ":active $description": styles.descriptionPressed,
                  ":active $menuIcon": styles.menuIconPressed,
                },
              },
            disabled && checked && [styles.rootCheckedDisabled],
            !disabled &&
              checked && {
                selectors: {
                  ":hover": styles.rootCheckedHovered,
                  ":active": styles.rootCheckedPressed,
                },
              },
          ],
          flexContainer: styles.flexContainer,
          textContainer: styles.textContainer,
          icon: [
            styles.icon,
            expanded && styles.iconExpanded,
            checked && styles.iconChecked,
            disabled && styles.iconDisabled,
          ],
          label: [
            styles.label,
            checked && styles.labelChecked,
            disabled && styles.labelDisabled,
          ],
          menuIcon: [
            styles.menuIcon,
            checked && styles.menuIconChecked,
            disabled && styles.menuIconDisabled,
            !disabled &&
              !expanded &&
              !checked && {
                selectors: {
                  ":hover": styles.menuIconHovered,
                  ":active": styles.menuIconPressed,
                },
              },
            expanded && [
              styles.menuIconExpanded,
              {
                selectors: {
                  ":hover": styles.menuIconExpandedHovered,
                },
              },
            ],
          ],
          description: [
            styles.description,
            checked && styles.descriptionChecked,
            disabled && styles.descriptionDisabled,
          ],
          screenReaderText: styles.screenReaderText,
        });
      }
    );
  }
);

export const getSplitButtonClassNames = memoizeFunction(
  (theme: ITheme, customStyles?: IButtonStyles, hasIcon: boolean = false) =>
    memoizeFunction(
      (
        disabled: boolean,
        expanded: boolean,
        checked: boolean,
        _allowDisabledFocus: boolean
      ): ISplitButtonClassNames => {
        const styles = mergeStyleSets(
          getAppSplitButtonStyles(theme, hasIcon),
          customStyles
        );
        return getFabricSplitButtonClassNames(
          styles,
          disabled,
          expanded,
          checked
        );
      }
    )
);

export const getAppPrimarySplitButtonStyles = memoizeFunction(
  (theme: ITheme, hasIcon: boolean = false): IButtonStyles => {
    const buttonStyles = getAppBaseButtonStyles(
      theme,
      undefined /* customStyles */,
      hasIcon
    );
    const standardControlSemanticColors: StandardControlSemanticColors =
      getStandardControlSemanticColorsWithTheme(theme);
    return concatStyleSets(buttonStyles, {
      root: [
        primaryButtonClassName,
        {
          padding: "0 12px 0 11px",
          margin: 0,
          height: buttonHeight,
          border: `1px solid ${standardControlSemanticColors.borderColor}`,
          borderRight: 0,
          borderRadius: "2px 0 0 2px",
          color: standardControlSemanticColors.textColor,
          selectors: {
            ":hover": commonHighContrastHoverFocusStyles,
          },
        },
      ],
      rootHovered: {
        outline: "none",
      },
      rootChecked: [
        {
          outline: "none",
          selector: {
            commonHighContrastHoverFocusStyles,
          },
          commonHighContrastHoverFocusStyles,
        },
        baseSplitButtonExpandedCheckedIconStyle(false /* disabled */),
      ],
      rootCheckedHovered: {
        outline: "none",
      },
      rootFocused: getCommonIsFocusVisibleStyles(theme),
      label: {
        marginRight: 2,
        fontWeight: FontWeights.semibold,
      },
      rootCheckedDisabled: [
        baseSplitButtonExpandedCheckedIconStyle(true /* disabled */),
      ],
    });
  }
);

const redlineAppSplitButtonStyles = (
  theme: ITheme,
  hasIcon: boolean = false
): IButtonStyles => {
  const standardControlSemanticColors: StandardControlSemanticColors =
    getStandardControlSemanticColorsWithTheme(theme);
  const buttonStyles = concatStyleSets(
    getAppBaseButtonStyles(theme, undefined /* customStyles */, hasIcon)
  );
  return {
    splitButtonDivider: {
      backgroundColor: standardControlSemanticColors.borderColor,
      right: menuButtonWidth,
      position: "absolute",
      alignSelf: "center",
      height: dividerHeight,
      width: 1,
    },
    splitButtonMenuButton: [
      menuButtonClassName,
      buttonStyles.root,
      {
        height: buttonHeight,
        width: menuButtonWidth,
        padding: 0,
        borderLeft: 0,
        borderRadius: "0 2px 2px 0",
        outline: "none",
      },
      {
        selectors: {
          ":hover": {
            selectors: splitButtonComponentHighContrastStyles,
          },
        },
      },
    ],
    splitButtonMenuButtonDisabled: buttonStyles.rootDisabled,
    splitButtonMenuButtonExpanded: [
      buttonStyles.rootExpanded,
      {
        selectors: {
          ":hover": {
            backgroundColor: standardControlSemanticColors.hoverBackgroundColor,
            selectors: splitButtonComponentHighContrastStyles,
          },
        },
      },
    ],
    splitButtonMenuIcon: {
      height: 12,
      lineHeight: 12,
      width: 12,
      fill: standardControlSemanticColors.textColor,
    },
    splitButtonMenuIconDisabled: {
      fill: standardControlSemanticColors.textDisabledColor,
    },
    splitButtonContainer: [
      {
        position: "relative",
        display: "inline-block",
        boxSizing: "border-box",
        border: "none",
        borderRadius: 2,
        height: 32,
        selectors: {
          ":focus:after": [
            getInnerBorderStyles({
              width: 2,
              color: standardControlSemanticColors.focusedBorderColor,
            }),
            { [HighContrastSelector]: { border: "none" } },
          ],
        },
      },
    ],
    splitButtonContainerHovered: [commonHighContrastOutlineHoverFocus],
    splitButtonContainerFocused: [
      commonHighContrastOutlineHoverFocus,
      {
        selectors: {
          [HighContrastSelector]: {
            backgroundColor: "Highlight",
            color: "HighlightText",
            outlineColor: "ButtonText",
            ...getHighContrastNoAdjustStyle(),
          },
          [`.${menuButtonClassName}`]: [
            {
              borderColor: standardControlSemanticColors.focusedBorderColor,
            },
            commonHighContrastHoverFocusStyles,
            baseSplitButtonExpandedCheckedIconStyle(false /* disabled */),
          ],
          [`.${primaryButtonClassName}`]: [
            {
              borderColor: standardControlSemanticColors.focusedBorderColor,
            },
            commonHighContrastHoverFocusStyles,
            baseSplitButtonExpandedCheckedIconStyle(false /* disabled */),
          ],
        },
      },
    ],
    splitButtonContainerChecked: [
      {
        selectors: {
          [":focus"]: {
            selectors: {
              [HighContrastSelector]: [
                commonHighContrastOutlineHoverFocus,
                commonHighContrastHoverFocusStyles,
              ],
            },
          },
          [`.${primaryButtonClassName}`]: [
            baseSplitButtonExpandedCheckedIconStyle(false /* disabled */),
          ],
          [`:focus .${menuButtonClassName}`]: {
            borderColor: standardControlSemanticColors.focusedBorderColor,
          },
          [`:focus .${primaryButtonClassName}`]: {
            borderColor: standardControlSemanticColors.focusedBorderColor,
            selectors: splitButtonComponentHighContrastStyles,
          },
        },
      },
      commonHighContrastOutlineToggledOn,
    ],
    splitButtonContainerCheckedHovered: [commonHighContrastOutlineHoverFocus],
    splitButtonContainerDisabled: {
      outline: 0,
      pointerEvents: "none",
      selectors: {
        [":focus"]: [
          commonHighContrastOutlineFocusedDisabled,
          commonHighContrastFocusedDisabledStyles,
          {
            [`.${menuButtonClassName}`]: [
              {
                borderColor: standardControlSemanticColors.focusedBorderColor,
              },
              commonHighContrastHoverFocusStyles,
              baseSplitButtonExpandedCheckedIconStyle(true /* disabled */),
            ],
          },
          {
            [`.${primaryButtonClassName}`]: [
              { borderColor: standardControlSemanticColors.focusedBorderColor },
              commonHighContrastHoverFocusStyles,
              baseSplitButtonExpandedCheckedIconStyle(true /* disabled */),
            ],
          },
        ],
        [":focus button"]: {
          borderColor: standardControlSemanticColors.focusedBorderColor,
          selectors: splitButtonComponentHighContrastStyles,
        },
      },
    },
  };
};

export const getAppSplitButtonStyles = memoizeFunction(
  (theme: ITheme, hasIcon: boolean = false): IButtonStyles =>
    redlineAppSplitButtonStyles(theme, hasIcon)
);
